import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Home, Calendar, Printer, Play, CheckCircle2, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABEL = {
  scheduled: { es: 'Asignada', class: 'bg-amber-500/20 text-amber-200 border-amber-400/40' },
  in_progress: { es: 'En curso', class: 'bg-blue-500/20 text-blue-200 border-blue-400/40' },
  completed: { es: 'Completada', class: 'bg-green-500/20 text-green-200 border-green-400/40' },
  cancelled: { es: 'Cancelada', class: 'bg-red-500/20 text-red-200 border-red-400/40' },
};

/**
 * MyFlexibleRoutines — Client tab listing assigned "Rutinas libres".
 *
 * Props:
 *   - userId: current client id
 *   - onStartWorkout: (booking|null, workout) => void   (reuses existing client execution path)
 *   - onViewResults:  (booking|null, workout) => void
 */
const MyFlexibleRoutines = ({ userId, refreshKey, onStartWorkout, onViewResults }) => {
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchRoutines = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/workouts?mode=flexible&summary=true`);
      setRoutines(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error cargando rutinas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRoutines(); }, [refreshKey]);

  const sorted = useMemo(() => {
    // Order: in_progress > scheduled > completed/cancelled, then most recent first
    const order = { in_progress: 0, scheduled: 1, completed: 2, cancelled: 3 };
    return [...routines].sort((a, b) => {
      const oa = order[a.status] ?? 9;
      const ob = order[b.status] ?? 9;
      if (oa !== ob) return oa - ob;
      return (b.created_at || '').localeCompare(a.created_at || '');
    });
  }, [routines]);

  const formatDate = (s) => {
    if (!s) return '';
    try {
      const d = new Date(s);
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
    } catch { return s; }
  };

  const handlePrint = (workout) => {
    window.open(`/workout/${workout.id}/print`, '_blank', 'noopener,noreferrer');
  };

  const handleStart = (workout) => {
    if (workout.status === 'completed') {
      onViewResults?.(null, workout);
    } else {
      onStartWorkout?.(null, workout);
    }
  };

  const [repeatingId, setRepeatingId] = useState(null);

  const handleRepeat = async (workout) => {
    if (repeatingId) return;
    setRepeatingId(workout.id);
    try {
      const d = new Date();
      const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const res = await axios.post(`${API}/workouts/${workout.id}/repeat-flexible`, { local_date: localDate });
      const fresh = res.data;
      await fetchRoutines();
      onStartWorkout?.(null, fresh);
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo repetir la rutina');
    } finally {
      setRepeatingId(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-blue-200" data-testid="my-flexible-loading">
        Cargando rutinas...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-400/30 rounded-lg p-4 text-red-200" data-testid="my-flexible-error">
        {error}
      </div>
    );
  }

  if (sorted.length === 0) {
    return (
      <div
        className="text-center py-12 text-blue-300 bg-blue-950/30 rounded-lg border border-blue-400/20"
        data-testid="my-flexible-empty"
      >
        <Home className="w-12 h-12 mx-auto mb-3 text-amber-300/60" />
        <h3 className="text-lg font-semibold text-white mb-1">Sin rutinas libres</h3>
        <p className="text-sm">
          Tu entrenador todavía no te ha asignado ninguna rutina libre.
          <br />Estas rutinas no requieren cita y puedes ejecutarlas cuando quieras.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="my-flexible-list">
      <div className="flex items-center gap-2 text-amber-200 text-sm bg-amber-500/10 border border-amber-400/30 rounded-lg p-3">
        <Home className="w-4 h-4 flex-shrink-0" />
        <span>
          <strong>Rutinas libres</strong> — entrena cuando quieras, sin cita.
          Puedes imprimir cualquiera para llevarla en papel.
        </span>
      </div>

      {sorted.map((w) => {
        const sLabel = STATUS_LABEL[w.status] || STATUS_LABEL.scheduled;
        const exercisesCount = w.exercise_count != null ? w.exercise_count : (w.exercises || []).length;
        const isDone = w.status === 'completed';
        const isInProgress = w.status === 'in_progress';
        const todayStr = new Date().toISOString().slice(0, 10);
        const isExpired = !isDone && (w.available_until || '').slice(0, 10) && (w.available_until).slice(0, 10) < todayStr;
        // A finished routine can be repeated while its window is still open
        // (or when it has no expiry). Each repeat becomes a new dated instance.
        const windowOpen = !w.available_until || (w.available_until).slice(0, 10) >= todayStr;
        const canRepeat = isDone && windowOpen;
        return (
          <div
            key={w.id}
            className="glass-card p-4 border-l-4 border-amber-400 hover:border-amber-300 transition"
            data-testid={`flexible-routine-card-${w.id}`}
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              {/* Left: Identity */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <Home className="w-4 h-4 text-amber-300 flex-shrink-0" />
                  <h3 className="text-white font-semibold truncate">
                    {w.template_name || 'Rutina libre'}
                  </h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${sLabel.class}`}>
                    {sLabel.es}
                  </span>
                  {isExpired && (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full border bg-slate-500/20 text-slate-300 border-slate-400/40"
                      data-testid={`flexible-expired-badge-${w.id}`}
                      title="El periodo de disponibilidad ha finalizado"
                    >
                      Caducada
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-blue-300">
                  <span>📋 {exercisesCount} ejercicios</span>
                  {w.template_focus && (
                    <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-200">
                      {w.template_focus}
                    </span>
                  )}
                  {(w.available_from || w.available_until) && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {w.available_from ? formatDate(w.available_from) : '...'}
                      {' → '}
                      {w.available_until ? formatDate(w.available_until) : '...'}
                    </span>
                  )}
                </div>
                {w.trainer_notes && (
                  <p className="text-sm text-blue-200/80 mt-2 italic line-clamp-2">
                    "{w.trainer_notes}"
                  </p>
                )}
              </div>

              {/* Right: Actions */}
              <div className="flex flex-col sm:items-end gap-2 flex-shrink-0">
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePrint(w)}
                    className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 text-sm transition"
                    title="Imprimir / Exportar PDF"
                    data-testid={`flexible-print-btn-${w.id}`}
                  >
                    <Printer className="w-4 h-4" />
                    <span className="hidden sm:inline">Imprimir</span>
                  </button>
                  <button
                    onClick={() => handleStart(w)}
                    className={`flex items-center gap-1 px-4 py-2 rounded-lg font-semibold text-sm transition ${
                      isDone
                        ? 'bg-green-500/20 text-green-200 hover:bg-green-500/30'
                        : isInProgress
                        ? 'bg-blue-500 text-white hover:bg-blue-400'
                        : 'bg-amber-500 text-black hover:bg-amber-400'
                    }`}
                    data-testid={`flexible-start-btn-${w.id}`}
                  >
                    {isDone ? (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        Ver resultados
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4" />
                        {isInProgress ? 'Continuar' : 'Empezar'}
                      </>
                    )}
                  </button>
                  {canRepeat && (
                    <button
                      onClick={() => handleRepeat(w)}
                      disabled={repeatingId === w.id}
                      className="flex items-center gap-1 px-4 py-2 rounded-lg font-semibold text-sm transition bg-amber-500/20 text-amber-200 hover:bg-amber-500/30 disabled:opacity-50"
                      title="Vuelve a hacer esta rutina — se guardará como una sesión nueva"
                      data-testid={`flexible-repeat-btn-${w.id}`}
                    >
                      <RefreshCw className={`w-4 h-4 ${repeatingId === w.id ? 'animate-spin' : ''}`} />
                      {repeatingId === w.id ? 'Creando...' : 'Repetir'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MyFlexibleRoutines;
